/*
    Examiner's view-only copy of a questionnaire a rater has not finished.

    PIN-324. An examiner wants to see what a parent or teacher has left to answer
    -- raters miss items, or are sure they submitted when they did not. The rater
    is the one filling the form in, so nothing here takes an answer or submits:
    two people editing one form would overwrite each other.

    It shows the form as it stood when the page loaded; reloading shows the
    rater's progress since.

    PIN-325. When the examiner gets the missing answers another way -- a parent on
    the phone -- they take the form over and finish it on the editable page, which
    locks the rater out.
*/

import { Fragment } from 'react';
import { Button, Modal, notification } from 'antd';
import { useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import QuestionnaireItem from '../examinee/questionnaire-item';
import QuestionnaireHeader from '../examinee/questionnaire-header';
import QuestionnairePartBreak from '../examinee/questionnaire-part-break';
import styles from '../../style/examinee/questionnaire-test-page.module.css';
import { useQuestionnaireSession } from '../../hooks/useQuestionnaireSession';
import { takeOverSession } from '../../utils/take-over-session';

const ExaminerQuestionnaireProgressPage = () => {
  const { sessionID } = useParams();
  const history = useHistory();
  const token = useSelector((state) => state.token);

  // requireTextResponses: what is "left" is what the rater's own form asks for.
  const {
    items,
    documents,
    responses,
    headerData,
    loading,
    loadError,
    answeredByItem,
    resumedRespondentName
  } = useQuestionnaireSession(sessionID, { requireTextResponses: true });

  const backLink = <Link to="/examiner/sessions">Back to Active Sessions</Link>;

  // Taken over here, before the editable page opens: that page sends a session
  // still marked as the rater's back to this one.
  const takeOver = () =>
    takeOverSession(sessionID, token)
      .then(() => history.push(`/examiner/questionnaire/${sessionID}`))
      .catch((error) => {
        console.error('Could not take the questionnaire over:', error);
        notification.error({
          message: 'This questionnaire could not be taken over',
          description: error.message
        });
      });

  const confirmTakeOver = () =>
    Modal.confirm({
      cancelText: 'Cancel',
      content:
        'You can then enter the answers yourself and submit. The rater will no longer be able to save answers to this form.',
      okText: 'Yes, take over',
      onOk: takeOver,
      title: 'Take over this questionnaire?'
    });

  if (loading) {
    return (
      <div className={styles.loading} data-testid="questionnaire-loading">
        Loading questionnaire...
      </div>
    );
  }

  // Most often the rater submitted after Active Sessions loaded, and the form is
  // no longer in progress. An empty page would read as "0 of 0 answered".
  if (loadError) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.viewOnlyNotice} data-testid="questionnaire-load-error">
            <p>
              <strong>This questionnaire could not be opened ({loadError}).</strong>
            </p>
            <p>The rater may have submitted it since Active Sessions loaded.</p>
            {backLink}
          </div>
        </div>
      </div>
    );
  }

  const statuses = Object.values(answeredByItem);

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.viewOnlyNotice} data-testid="questionnaire-view-only-notice">
          <p>
            <strong>View only — the rater is completing this form.</strong>
          </p>
          <p data-testid="questionnaire-progress-count">
            {statuses.filter(Boolean).length} of {statuses.length} items answered
          </p>
          <p>
            <Button onClick={confirmTakeOver}>Take over and complete</Button>
          </p>
          {backLink}
        </div>

        <QuestionnaireHeader
          headerData={headerData}
          resumedParentName={resumedRespondentName}
          readOnly
        />

        <div className={styles.itemList} data-testid="questionnaire-item-list">
          {items.map((item, i) => (
            <Fragment key={item._id}>
              <QuestionnairePartBreak
                item={item}
                previousItem={items[i - 1]}
                parts={headerData?.parts}
              />
              <QuestionnaireItem
                index={i + 1}
                item={item}
                document={documents[item._id]}
                responses={responses}
                answered={answeredByItem[item._id]}
                readOnly
              />
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExaminerQuestionnaireProgressPage;
